import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import TargetCardsGrid from './TargetCardsGrid';
import TargetDetailModal from './TargetDetailModal';
import MasterTimeline from './MasterTimeline';
import ScrollFix from './ScrollFix';
import SimilarJobsPanel from '../results/display/panels/SimilarJobsPanel';
import ErrorLogPanel from './ErrorLogPanel';
import LegalReportTab from './tabs/LegalReportTab';
import '../../styles/v4-theme.css';
import '../../styles/v4-mobile-fixes.css';
import '../../styles/v4-absolute-scroll-fix.css';
import '../../styles/v4-final-scroll-fix.css';
import '../../styles/precision-enhancements.css';

/**
 * V4AnalysisDisplay Component
 * Main container for V4 target-centric analysis visualization
 * Replaces the "Coming Soon" placeholders with full V4 display system
 */
const V4AnalysisDisplay = ({ 
  analysisData, 
  onTimestampClick,
  selectedModalTarget = null,
  setSelectedModalTarget = null,
  viewMode: liftedViewMode = null,
  setViewMode: setLiftedViewMode = null,
  job = null
}) => {
  // Use lifted state if provided, otherwise fall back to local state
  const [localSelectedTarget, setLocalSelectedTarget] = useState(null);
  const [localViewMode, setLocalViewMode] = useState('grid');
  
  const isUsingLiftedState = setSelectedModalTarget !== null && setLiftedViewMode !== null;
  const selectedTarget = isUsingLiftedState ? selectedModalTarget : localSelectedTarget;
  const setSelectedTarget = isUsingLiftedState ? setSelectedModalTarget : setLocalSelectedTarget;
  const viewMode = isUsingLiftedState ? liftedViewMode : localViewMode;
  const setViewMode = isUsingLiftedState ? setLiftedViewMode : setLocalViewMode;
  
  const [activeTab, setActiveTab] = useState(null); // Will auto-select first available tab
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  
  const tabsContainerRef = useRef(null);

  // Add v4-modal-active class to body when this component is mounted
  // This ensures scroll-blocking styles only apply when V4 display is active
  useEffect(() => {
    document.body.classList.add('v4-modal-active');
    
    return () => {
      document.body.classList.remove('v4-modal-active');
    };
  }, []);


  // Extract data from analysis (memoized to prevent unnecessary re-renders)
  const targets = useMemo(() => analysisData?.targets || [], [analysisData?.targets]);
  const masterTimeline = useMemo(() => analysisData?.master_timeline || [], [analysisData?.master_timeline]);

  // Group targets by type (memoized to prevent unnecessary re-renders)
  // Support both 'type' and 'target_type' field names for backward compatibility
  const targetsByType = useMemo(() => ({
    vehicles: targets.filter(t => t.type === 'vehicle' || t.target_type === 'vehicle'),
    environment: targets.filter(t => t.type === 'environment' || t.target_type === 'environment'),
    videographer: targets.filter(t => t.type === 'videographer' || t.target_type === 'videographer'),
    people: targets.filter(t => t.type === 'person' || t.target_type === 'person'),
    unknown: targets.filter(t => (t.type === 'unknown' || t.target_type === 'unknown') || (!t.type && !t.target_type)), // Catch unknown targets
    all: targets
  }), [targets]);


  // Handle target card click - Navigate to detail view instead of modal
  const handleTargetClick = (target) => {
    setSelectedTarget(target);
    setViewMode('detail');
  };

  // Handle back to grid
  const handleBackToGrid = () => {
    const singletonTypes = ['environment', 'videographer'];
    const targetType = selectedTarget?.type || selectedTarget?.target_type;
    
    // If returning from a singleton target, switch to a different tab
    if (selectedTarget && singletonTypes.includes(targetType)) {
      console.log('🎨 Returning from singleton target, switching tab');
      
      // Find first non-singleton tab with data
      const nonSingletonTab = visibleTabs.find(tab => 
        !singletonTypes.includes(tab.id) && tab.id !== 'timeline' && tab.count > 0
      );
      
      // Switch to that tab, or timeline if no other tabs exist
      if (nonSingletonTab) {
        setActiveTab(nonSingletonTab.id);
      } else if (visibleTabs.find(t => t.id === 'timeline')) {
        setActiveTab('timeline');
      }
    }
    
    setViewMode('grid');
    setSelectedTarget(null);
  };

  // Handle timeline event click
  const handleTimelineEventClick = (event) => {
    // Find the target associated with this event
    const target = targets.find(t => t.id === event.target_id);
    if (target) {
      handleTargetClick(target);
    }
  };

  // Get targets for active tab
  const getActiveTargets = () => {
    if (activeTab === 'timeline') return [];
    return targetsByType[activeTab] || [];
  };

  const activeTargets = getActiveTargets();

  // Count verified vehicles (for Legal Report tab)
  const verifiedVehicleCount = useMemo(() => {
    return targetsByType.vehicles.filter(v => 
      v.anchor_type === 'PRIMARY' && 
      v.attributes?.license_plate && 
      v.attributes.license_plate !== 'Not determinable'
    ).length;
  }, [targetsByType.vehicles]);

  // Tab definitions
  const tabs = [
    { id: 'vehicles', label: 'Vehicles', icon: '◆', count: targetsByType.vehicles.length },
    { id: 'environment', label: 'Environment', icon: '▲', count: targetsByType.environment.length },
    { id: 'videographer', label: 'Videographer', icon: '■', count: targetsByType.videographer.length },
    { id: 'unknown', label: 'Unknown', icon: '○', count: targetsByType.unknown.length },
    { id: 'timeline', label: 'Timeline', icon: '▬', count: masterTimeline.length },
    // Legal Report tab (shows verified vehicles count)
    { id: 'legal', label: 'Legal Report', icon: '⚖️', count: verifiedVehicleCount },
    // Utility tabs (always visible)
    { id: 'similar', label: 'Similar Jobs', icon: '🔗', alwaysVisible: true },
    { id: 'json', label: 'Full JSON', icon: '{}', alwaysVisible: true },
    { id: 'raw', label: 'Raw Output', icon: '📄', alwaysVisible: true }
  ];

  // Show tabs that have data OR are marked as always visible
  const visibleTabs = tabs.filter(tab => tab.count > 0 || tab.alwaysVisible);

  // Check if scroll arrows are needed
  const checkScrollArrows = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;
    
    const scrollLeft = container.scrollLeft;
    const canScrollLeft = scrollLeft > 5;
    
    // Show left arrow only when scrolled right
    setShowLeftArrow(canScrollLeft);
  }, []);

  // Scroll tabs left or right
  const scrollTabs = (direction) => {
    const container = tabsContainerRef.current;
    if (!container) return;
    
    const scrollAmount = 200;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
    
    setTimeout(checkScrollArrows, 300);
  };

  // Auto-select first tab with data if none selected
  React.useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  // Check scroll arrows on mount and when tabs change
  useEffect(() => {
    const timer = setTimeout(checkScrollArrows, 100);
    window.addEventListener('resize', checkScrollArrows);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkScrollArrows);
    };
  }, [visibleTabs.length, checkScrollArrows]);

  // Auto-show details for singleton targets (environment, videographer)
  React.useEffect(() => {
    const singletonTypes = ['environment', 'videographer'];
    
    // If we're on a singleton tab and in grid view
    if (singletonTypes.includes(activeTab) && viewMode === 'grid') {
      const targets = targetsByType[activeTab] || [];
      
      // If there's exactly 1 target, auto-show its details
      if (targets.length === 1) {
        setSelectedTarget(targets[0]);
        setViewMode('detail');
      }
    }
  }, [activeTab, viewMode, targetsByType]);


  // Check if this is a precision-enhanced scan
  const isPrecisionScan = analysisData?.precision_enhanced === true;
  const videoContinuity = analysisData?.video_continuity;

  return (
    <div className="v4-container">
      <ScrollFix />
      {/* GRID VIEW - Show target cards and tabs */}
      {viewMode === 'grid' && (
        <>
          {/* Precision Badge - Only show for precision scans */}
          {isPrecisionScan && (
            <div style={{ marginBottom: 'var(--space-2)' }}>
              <div className="precision-badge">
                <span className="precision-badge-icon">🎯</span>
                <span className="precision-badge-text">PRECISION</span>
                <span className="precision-badge-accuracy">±0.001s</span>
              </div>
            </div>
          )}

          {/* Continuity Banner - Only show for precision scans with continuity data */}
          {isPrecisionScan && videoContinuity && (
            <div className={`continuity-banner ${videoContinuity.is_continuous ? 'continuous' : 'edited'}`}>
              <span className="continuity-icon">
                {videoContinuity.is_continuous ? '✓' : '⚠️'}
              </span>
              <span className="continuity-text">
                {videoContinuity.is_continuous ? 'CONTINUOUS VIDEO' : 'EDITED VIDEO'}
              </span>
              <span className="continuity-detail">
                {videoContinuity.is_continuous 
                  ? `No cuts or edits detected (${videoContinuity.shot_count} shot)`
                  : `${videoContinuity.shot_count} shots detected${videoContinuity.cuts_detected_at && videoContinuity.cuts_detected_at.length > 0 ? `. Cuts at: ${videoContinuity.cuts_detected_at.join(', ')}` : ''}`
                }
              </span>
            </div>
          )}

          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2)',
            background: 'var(--bg-secondary)',
            borderRadius: '8px',
            marginBottom: 'var(--space-2)',
            position: 'relative',
            zIndex: 100
          }}>
            {visibleTabs.length >= 3 && (
              <button
                onClick={() => scrollTabs('right')}
                aria-label="Scroll tabs right"
                style={{
                  background: 'rgba(124, 58, 237, 0.25)',
                  border: '1px solid rgba(124, 58, 237, 0.4)',
                  color: '#a78bfa',
                  borderRadius: '4px',
                  width: '24px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  flexShrink: 0,
                  padding: 0,
                  fontSize: '10px',
                  zIndex: 101,
                  opacity: showLeftArrow ? 0.3 : 0.7
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = showLeftArrow ? '0.3' : '0.7'}
              >
                <FaChevronRight />
              </button>
            )}
            <div 
              ref={tabsContainerRef}
              onScroll={checkScrollArrows}
              className="v4-hide-scrollbar"
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                flex: 1
              }}
            >
              <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                minWidth: 'min-content'
              }}>
                {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: '0 0 auto',
                    minWidth: 'fit-content',
                    padding: 'var(--space-3) var(--space-4)',
                    background: activeTab === tab.id ? 'var(--accent)' : 'var(--bg-card)',
                    color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
                    border: `1px solid ${activeTab === tab.id ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: '8px',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                    cursor: 'pointer',
                    transition: 'all var(--transition-base)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-2)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                  <span style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-bold)'
                  }}>
                    {tab.count}
                  </span>
                </button>
                ))}
              </div>
            </div>
            {visibleTabs.length >= 3 && (
              <button
                onClick={() => scrollTabs('left')}
                aria-label="Scroll tabs left"
                style={{
                  background: 'rgba(124, 58, 237, 0.25)',
                  border: '1px solid rgba(124, 58, 237, 0.4)',
                  color: '#a78bfa',
                  borderRadius: '4px',
                  width: '24px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  flexShrink: 0,
                  padding: 0,
                  fontSize: '10px',
                  zIndex: 101,
                  opacity: showLeftArrow ? 0.7 : 0.3
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = showLeftArrow ? '0.7' : '0.3'}
              >
                <FaChevronLeft />
              </button>
            )}
          </div>

          {/* Tab Content */}
          {activeTab === 'timeline' ? (
            /* Timeline Tab */
            <MasterTimeline
              timeline={masterTimeline}
              onEventClick={handleTimelineEventClick}
              onTimestampClick={onTimestampClick}
            />
          ) : activeTab === 'legal' ? (
            /* Legal Report Tab */
            <LegalReportTab
              targets={targets}
              analysisData={analysisData}
              job={job}
            />
          ) : activeTab === 'similar' ? (
            /* Similar Jobs Tab */
            <div style={{ padding: 'var(--space-3)' }}>
              <SimilarJobsPanel
                currentJobId={job?.id}
                currentResults={analysisData ? { structuredData: analysisData } : null}
                onJobSelect={(similarJob) => {
                  console.log('Similar job selected:', similarJob);
                  // Could trigger navigation or modal here
                }}
              />
            </div>
          ) : activeTab === 'json' ? (
            /* Full JSON Tab */
            <div style={{ padding: 'var(--space-3)' }}>
              <div style={{
                background: 'var(--bg-card)',
                borderRadius: '8px',
                padding: 'var(--space-3)',
                border: '1px solid var(--border)'
              }}>
                <h3 style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-bold)',
                  color: 'var(--text-primary)',
                  marginBottom: 'var(--space-2)',
                  fontFamily: 'Courier New, monospace'
                }}>
                  Complete Analysis JSON
                </h3>
                <pre style={{
                  fontSize: '11px',
                  lineHeight: '1.5',
                  overflow: 'auto',
                  maxHeight: '70vh',
                  background: 'var(--bg-secondary)',
                  padding: 'var(--space-2)',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  color: 'var(--text-secondary)',
                  fontFamily: 'Courier New, monospace'
                }}>
                  {JSON.stringify(analysisData, null, 2)}
                </pre>
              </div>
            </div>
          ) : activeTab === 'raw' ? (
            /* Raw Output Tab */
            <div style={{ padding: 'var(--space-3)' }}>
              <div style={{
                background: 'var(--bg-card)',
                borderRadius: '8px',
                padding: 'var(--space-3)',
                border: '1px solid var(--border)'
              }}>
                <h3 style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-bold)',
                  color: 'var(--text-primary)',
                  marginBottom: 'var(--space-2)',
                  fontFamily: 'Courier New, monospace'
                }}>
                  Raw API Response
                </h3>
                {job?.rawOutput ? (
                  <pre style={{
                    fontSize: '11px',
                    lineHeight: '1.5',
                    overflow: 'auto',
                    maxHeight: '70vh',
                    background: 'var(--bg-secondary)',
                    padding: 'var(--space-2)',
                    borderRadius: '6px',
                    border: '1px solid var(--border)',
                    color: 'var(--text-secondary)',
                    fontFamily: 'Courier New, monospace'
                  }}>
                    {JSON.stringify(job.rawOutput, null, 2)}
                  </pre>
                ) : (
                  <div style={{
                    padding: 'var(--space-4)',
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    background: 'var(--bg-secondary)',
                    borderRadius: '6px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: 'var(--space-2)' }}>📄</div>
                    <div style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-1)' }}>
                      No raw output available
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', opacity: 0.7 }}>
                      This job might have been created before raw output was saved,<br />
                      or the data wasn't stored in the database.
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Target Cards for Selected Tab */
            <>
              <TargetCardsGrid
                targets={activeTargets}
                onTargetClick={handleTargetClick}
                onTimestampClick={onTimestampClick}
                selectedTargetId={selectedTarget?.id}
              />
              
              {/* Error Log Panel - Only show for precision scans with error data */}
              {isPrecisionScan && analysisData?.error_log && (
                <ErrorLogPanel errorLog={analysisData.error_log} />
              )}
            </>
          )}
        </>
      )}

      {/* DETAIL VIEW - Show single target details with back button */}
      {viewMode === 'detail' && selectedTarget && (
        <div className="v4-detail-view">
          {/* Back Button Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-2)',
            background: 'var(--bg-secondary)',
            borderRadius: '8px',
            marginBottom: 'var(--space-2)',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <button
              onClick={handleBackToGrid}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                cursor: 'pointer',
                transition: 'all var(--transition-base)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--accent)';
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--bg-card)';
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.color = 'var(--text-primary)';
              }}
            >
              <span>←</span>
              <span>Back to All Targets</span>
            </button>
            <div style={{
              flex: 1,
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--text-primary)'
            }}>
              {(selectedTarget.type === 'vehicle' || selectedTarget.target_type === 'vehicle') && (
                `${selectedTarget.attributes?.color || ''} ${selectedTarget.attributes?.make || ''} ${selectedTarget.attributes?.model || ''}`.trim() || 'Vehicle'
              )}
              {(selectedTarget.type === 'environment' || selectedTarget.target_type === 'environment') && 'Environment'}
              {(selectedTarget.type === 'videographer' || selectedTarget.target_type === 'videographer') && 'Videographer'}
            </div>
          </div>

          {/* Target Detail Content (Inline - No Modal) */}
          <TargetDetailModal
            target={selectedTarget}
            isOpen={true}
            onClose={handleBackToGrid}
            onTimestampClick={onTimestampClick}
            inline={true}
            masterTimeline={masterTimeline}
          />
        </div>
      )}
    </div>
  );
};

export default V4AnalysisDisplay;

